/**
 * AWS Health API client for fetching Bedrock model deprecation events.
 * Uses the customer's AWS credentials to query PHD (Personal Health Dashboard).
 * Health API is only available in us-east-1 (global endpoint).
 */

import { credentialsManager } from './credentials-manager'

export interface HealthEvent {
  eventArn: string
  service: string
  eventTypeCode: string
  eventTypeCategory: string
  region: string
  startTime: string
  endTime?: string
  lastUpdatedTime: string
  statusCode: string
  description?: string
  affectedEntities: AffectedEntity[]
}

export interface AffectedEntity {
  entityValue: string  // model ID or resource
  statusCode?: string
  lastUpdatedTime?: string
}

export interface HealthApiResult {
  events: HealthEvent[]
  error?: string
  permissionDenied?: boolean
}

export async function fetchBedrockHealthEvents(): Promise<HealthApiResult> {
  const creds = await credentialsManager.getAwsCredentials()
  if (!creds) {
    return { events: [], error: 'No AWS credentials configured. Go to Settings to add credentials.' }
  }

  try {
    const { HealthClient, DescribeEventsCommand, DescribeEventDetailsCommand, DescribeAffectedEntitiesCommand } = await import('@aws-sdk/client-health')

    const client = new HealthClient({
      region: 'us-east-1', // Health API is global, only in us-east-1
      credentials: {
        accessKeyId: creds.accessKeyId,
        secretAccessKey: creds.secretAccessKey,
        ...(creds.sessionToken ? { sessionToken: creds.sessionToken } : {}),
      },
    })

    // Fetch Bedrock events — scheduled changes and account notifications
    const eventsResponse = await client.send(new DescribeEventsCommand({
      filter: {
        services: ['BEDROCK'],
        eventTypeCategories: ['scheduledChange', 'accountNotification'],
        eventStatusCodes: ['open', 'upcoming', 'closed'],
      },
    }))

    const rawEvents = eventsResponse.events || []
    if (rawEvents.length === 0) {
      return { events: [] }
    }

    // Fetch details and affected entities for each event
    const events: HealthEvent[] = []

    // Get event details in batch (max 10 per call)
    const eventArns = rawEvents.map(e => e.arn!).filter(Boolean)
    const detailsMap = new Map<string, string>()
    const entitiesMap = new Map<string, AffectedEntity[]>()

    for (let i = 0; i < eventArns.length; i += 10) {
      const batch = eventArns.slice(i, i + 10)

      try {
        const detailsResp = await client.send(new DescribeEventDetailsCommand({
          eventArns: batch,
        }))
        for (const detail of detailsResp.successfulSet || []) {
          if (detail.event?.arn && detail.eventDescription?.latestDescription) {
            detailsMap.set(detail.event.arn, detail.eventDescription.latestDescription)
          }
        }
      } catch { /* some events may not have details */ }

      try {
        for (const arn of batch) {
          const entitiesResp = await client.send(new DescribeAffectedEntitiesCommand({
            filter: { eventArns: [arn] },
          }))
          const entities = (entitiesResp.entities || []).map(e => ({
            entityValue: e.entityValue || '',
            statusCode: e.statusCode,
            lastUpdatedTime: e.lastUpdatedTime?.toISOString(),
          }))
          entitiesMap.set(arn, entities)
        }
      } catch { /* ignore entity fetch errors */ }
    }

    for (const e of rawEvents) {
      events.push({
        eventArn: e.arn || '',
        service: e.service || 'BEDROCK',
        eventTypeCode: e.eventTypeCode || '',
        eventTypeCategory: e.eventTypeCategory || '',
        region: e.region || 'global',
        startTime: e.startTime?.toISOString() || '',
        endTime: e.endTime?.toISOString(),
        lastUpdatedTime: e.lastUpdatedTime?.toISOString() || '',
        statusCode: e.statusCode || '',
        description: detailsMap.get(e.arn || ''),
        affectedEntities: entitiesMap.get(e.arn || '') || [],
      })
    }

    // Sort by start time descending (most recent first)
    events.sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime())

    return { events }
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : String(err)
    const name = (err as { name?: string })?.name || ''

    if (name === 'AccessDeniedException' || message.includes('not authorized') || message.includes('Access Denied')) {
      return {
        events: [],
        error: 'Access denied for AWS Health API. Your IAM role needs health:DescribeEvents, health:DescribeEventDetails, and health:DescribeAffectedEntities permissions.',
        permissionDenied: true,
      }
    }

    // Health API requires Business/Enterprise support plan
    if (message.includes('SubscriptionRequiredException') || name === 'SubscriptionRequiredException') {
      return {
        events: [],
        error: 'AWS Health API requires a Business or Enterprise Support plan. Your account does not have the required support level.',
        permissionDenied: true,
      }
    }

    return { events: [], error: `Health API error: ${message}` }
  }
}
